import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { formatDistanceToNow } from 'date-fns';
import {
  Plus,
  Image as ImageIcon,
  Trash2,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Camera,
} from 'lucide-react';

interface StatusRow {
  id: string;
  user_id: string;
  content: string | null;
  media_url: string | null;
  media_type: string | null;
  background_color: string | null;
  created_at: string;
  expires_at: string;
}

interface ProfileLite {
  id: string;
  name: string;
  profile_image: string | null;
}

interface StatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, jump straight into the story viewer for this user on open */
  initialUserId?: string;
}

const BG_COLORS = [
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#EF4444',
  '#F59E0B',
  '#10B981',
  '#0EA5E9',
  '#1F2937',
];

const STORY_DURATION_MS = 5000;

async function resolveSignedUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  try {
    const { data, error } = await supabase.storage
      .from('chat-attachments')
      .createSignedUrl(path, 60 * 60);
    if (error) return null;
    return data?.signedUrl ?? null;
  } catch {
    return null;
  }
}

export function StatusDialog({ open, onOpenChange, initialUserId }: StatusDialogProps) {
  const { user } = useAuth();
  const { profile: ownProfile } = useProfile();
  const { t } = useLanguage();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState<StatusRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [text, setText] = useState('');
  const [bgColor, setBgColor] = useState(BG_COLORS[0]);
  const [posting, setPosting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Viewer state: when set, show full-screen story viewer for a user
  const [viewerUserId, setViewerUserId] = useState<string | null>(null);
  const [viewerIndex, setViewerIndex] = useState(0);

  const fetchStatuses = async (jumpToUserId?: string) => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('statuses')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;

      const rows = (data || []) as StatusRow[];
      setStatuses(rows);

      // Seed own profile from useProfile hook so name/avatar is always correct
      const map: Record<string, ProfileLite> = {};
      if (user && ownProfile) {
        map[user.id] = {
          id: user.id,
          name: ownProfile.name,
          profile_image: ownProfile.profile_image ?? null,
        };
      }

      // Load other users' profiles via RPC (bypasses RLS restrictions on profiles table)
      const otherIds = Array.from(new Set(rows.map((r) => r.user_id))).filter(
        (id) => id !== user.id
      );
      if (otherIds.length > 0) {
        const results = await Promise.allSettled(
          otherIds.map((id) =>
            supabase.rpc('get_chat_participant_profile', { _profile_id: id })
          )
        );
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value.data && r.value.data.length > 0) {
            const p = r.value.data[0];
            map[otherIds[i]] = { id: p.id, name: p.name, profile_image: p.profile_image };
          }
        });
      }

      setProfiles(map);

      // If asked to jump straight into a viewer, do so now that statuses are loaded
      if (jumpToUserId) {
        const grouped = new Map<string, StatusRow[]>();
        rows.forEach((s) => {
          const arr = grouped.get(s.user_id) || [];
          arr.push(s);
          grouped.set(s.user_id, arr);
        });
        if (grouped.has(jumpToUserId)) {
          setViewerUserId(jumpToUserId);
          setViewerIndex(0);
        }
      }
    } catch (e) {
      console.error('[StatusDialog] fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setComposerOpen(false);
      setViewerUserId(null);
      fetchStatuses(initialUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialUserId]);

  // Group statuses by user
  const grouped = useMemo(() => {
    const map = new Map<string, StatusRow[]>();
    statuses.forEach((s) => {
      const arr = map.get(s.user_id) || [];
      arr.push(s);
      map.set(s.user_id, arr);
    });
    return map;
  }, [statuses]);

  const myStatuses = user ? grouped.get(user.id) || [] : [];
  const otherUserIds = Array.from(grouped.keys()).filter((id) => id !== user?.id);

  const handlePostText = async () => {
    if (!user || (!text.trim())) return;
    setPosting(true);
    try {
      const { error } = await supabase.from('statuses').insert({
        user_id: user.id,
        content: text.trim(),
        background_color: bgColor,
      });
      if (error) throw error;
      toast({ title: 'Status posted', description: 'Disappears in 24 hours' });
      setText('');
      setComposerOpen(false);
      await fetchStatuses();
    } catch (e: any) {
      toast({
        title: 'Failed to post',
        description: e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setPosting(false);
    }
  };

  const handlePostImage = async (file: File) => {
    if (!user) return;
    setPosting(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `status/${user.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('chat-attachments')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'image/jpeg',
        });
      if (upErr) throw upErr;

      const { error } = await supabase.from('statuses').insert({
        user_id: user.id,
        content: text.trim() || null,
        media_url: path,
        media_type: file.type.startsWith('video') ? 'video' : 'image',
        background_color: bgColor,
      });
      if (error) throw error;

      toast({ title: 'Status posted', description: 'Disappears in 24 hours' });
      setText('');
      setComposerOpen(false);
      await fetchStatuses();
    } catch (e: any) {
      toast({
        title: 'Failed to post',
        description: e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteStatus = async (id: string) => {
    try {
      const { error } = await supabase.from('statuses').delete().eq('id', id);
      if (error) throw error;
      setStatuses((prev) => prev.filter((s) => s.id !== id));
      toast({ title: 'Status deleted' });
    } catch (e: any) {
      toast({
        title: 'Failed to delete',
        description: e?.message || '',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Dialog open={open && !viewerUserId} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Status</DialogTitle>
          </DialogHeader>

          {composerOpen ? (
            <Composer
              text={text}
              setText={setText}
              bgColor={bgColor}
              setBgColor={setBgColor}
              posting={posting}
              onCancel={() => setComposerOpen(false)}
              onPost={handlePostText}
              onPickImage={() => fileInputRef.current?.click()}
            />
          ) : (
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {/* My status */}
                <button
                  type="button"
                  className="w-full flex items-center gap-3 hover:bg-accent rounded-lg p-2 -m-2 text-left"
                  onClick={() => {
                    if (myStatuses.length > 0) {
                      setViewerUserId(user!.id);
                      setViewerIndex(0);
                    } else {
                      setComposerOpen(true);
                    }
                  }}
                >
                  <div className="relative">
                    {myStatuses.length > 0 ? (
                      <div className="rounded-full p-[2px] bg-gradient-to-tr from-primary to-purple-500">
                        <Avatar className="h-14 w-14 border-2 border-background">
                          <AvatarImage src={ownProfile?.profile_image || undefined} />
                          <AvatarFallback>
                            {(ownProfile?.name || 'M')[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    ) : (
                      <Avatar className="h-14 w-14">
                        <AvatarImage src={ownProfile?.profile_image || undefined} />
                        <AvatarFallback>
                          {(ownProfile?.name || 'M')[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1 border-2 border-background">
                      <Plus className="h-3 w-3 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{ownProfile?.name || 'My status'}</p>
                    <p className="text-xs text-muted-foreground">
                      {myStatuses.length > 0
                        ? `${myStatuses.length} update${myStatuses.length > 1 ? 's' : ''} • ${formatDistanceToNow(new Date(myStatuses[myStatuses.length - 1].created_at), { addSuffix: true })}`
                        : 'Tap to add status update'}
                    </p>
                  </div>
                </button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setComposerOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" /> New status
                </Button>

                {/* Recent updates from contacts */}
                {otherUserIds.length > 0 && (
                  <div className="pt-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      Recent updates
                    </p>
                    <div className="space-y-1">
                      {otherUserIds.map((uid) => {
                        const items = grouped.get(uid) || [];
                        const last = items[items.length - 1];
                        const prof = profiles[uid];
                        return (
                          <button
                            key={uid}
                            type="button"
                            className="w-full flex items-center gap-3 hover:bg-accent rounded-lg p-2 text-left"
                            onClick={() => {
                              setViewerUserId(uid);
                              setViewerIndex(0);
                            }}
                          >
                            <div className="relative rounded-full p-[2px] bg-gradient-to-tr from-primary to-purple-500">
                              <Avatar className="h-12 w-12 border-2 border-background">
                                <AvatarImage src={prof?.profile_image || undefined} />
                                <AvatarFallback>
                                  {(prof?.name || 'U')[0]?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{prof?.name || 'User'}</p>
                              <p className="text-xs text-muted-foreground">
                                {items.length} update{items.length > 1 ? 's' : ''} •{' '}
                                {formatDistanceToNow(new Date(last.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {loading && (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {!loading && statuses.length === 0 && (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No status updates yet. Be the first to share!
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handlePostImage(f);
              e.currentTarget.value = '';
            }}
          />
        </DialogContent>
      </Dialog>

      {viewerUserId && (
        <StoryViewer
          userId={viewerUserId}
          items={grouped.get(viewerUserId) || []}
          startIndex={viewerIndex}
          profile={profiles[viewerUserId]}
          isOwn={viewerUserId === user?.id}
          onClose={() => {
            setViewerUserId(null);
            // Refresh to update view counts
            fetchStatuses();
          }}
          onDelete={handleDeleteStatus}
        />
      )}
    </>
  );
}

function Composer({
  text,
  setText,
  bgColor,
  setBgColor,
  posting,
  onCancel,
  onPost,
  onPickImage,
}: {
  text: string;
  setText: (v: string) => void;
  bgColor: string;
  setBgColor: (v: string) => void;
  posting: boolean;
  onCancel: () => void;
  onPost: () => void;
  onPickImage: () => void;
}) {
  return (
    <div className="flex flex-col flex-1">
      <div
        className="relative flex items-center justify-center p-8 min-h-[260px] transition-colors"
        style={{ backgroundColor: bgColor }}
      >
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What's on your mind?"
          maxLength={300}
          className="bg-transparent border-none text-white text-xl text-center placeholder:text-white/70 resize-none focus-visible:ring-0 shadow-none"
          rows={4}
        />
      </div>
      <div className="p-4 space-y-3">
        <div className="flex gap-2 items-center justify-center flex-wrap">
          {BG_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setBgColor(c)}
              className={`h-8 w-8 rounded-full border-2 transition-all ${
                bgColor === c ? 'border-foreground scale-110' : 'border-transparent'
              }`}
              style={{ backgroundColor: c }}
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onPickImage} disabled={posting}>
            <Camera className="h-4 w-4 mr-2" /> Photo
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={posting}>
            Cancel
          </Button>
          <Button onClick={onPost} disabled={posting || !text.trim()}>
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StoryViewer({
  userId,
  items,
  startIndex,
  profile,
  isOwn,
  onClose,
  onDelete,
}: {
  userId: string;
  items: StatusRow[];
  startIndex: number;
  profile: ProfileLite | undefined;
  isOwn: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
}) {
  const [index, setIndex] = useState(startIndex);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [viewCount, setViewCount] = useState<number | null>(null);
  const current = items[index];

  // Resolve media url
  useEffect(() => {
    let active = true;
    setMediaUrl(null);
    if (current?.media_url) {
      resolveSignedUrl(current.media_url).then((u) => {
        if (active) setMediaUrl(u);
      });
    }
    return () => {
      active = false;
    };
  }, [current?.id, current?.media_url]);

  // Mark as viewed + load view count if own
  useEffect(() => {
    if (!current) return;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) return;
      if (uid !== current.user_id) {
        await supabase
          .from('status_views')
          .insert({ status_id: current.id, viewer_id: uid });
      }
      if (isOwn) {
        const { count } = await supabase
          .from('status_views')
          .select('*', { count: 'exact', head: true })
          .eq('status_id', current.id);
        setViewCount(count ?? 0);
      } else {
        setViewCount(null);
      }
    })();
  }, [current?.id, isOwn]);

  // Progress timer
  useEffect(() => {
    setProgress(0);
    if (!current || paused) return;
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(100, (elapsed / STORY_DURATION_MS) * 100);
      setProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        if (index < items.length - 1) {
          setIndex(index + 1);
        } else {
          onClose();
        }
      }
    }, 50);
    return () => clearInterval(interval);
  }, [index, paused, current?.id]);

  if (!current) {
    return null;
  }

  const goPrev = () => {
    if (index > 0) setIndex(index - 1);
  };
  const goNext = () => {
    if (index < items.length - 1) setIndex(index + 1);
    else onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black flex flex-col safe-area-screen"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      {/* Progress bars */}
      <div className="flex gap-1 p-2 pt-4">
        {items.map((_, i) => (
          <div key={i} className="flex-1 h-1 bg-white/30 rounded overflow-hidden">
            <div
              className="h-full bg-white transition-[width] duration-75"
              style={{
                width: i < index ? '100%' : i === index ? `${progress}%` : '0%',
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-8 w-8">
            <AvatarImage src={profile?.profile_image || undefined} />
            <AvatarFallback>{(profile?.name || 'U')[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              {isOwn ? 'You' : profile?.name || 'User'}
            </p>
            <p className="text-xs text-white/70">
              {formatDistanceToNow(new Date(current.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isOwn && (
            <Button
              size="icon"
              variant="ghost"
              className="text-white hover:bg-white/10 hover:text-white"
              onClick={() => {
                onDelete(current.id);
                if (items.length === 1) onClose();
                else if (index >= items.length - 1) setIndex(Math.max(0, index - 1));
              }}
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="text-white hover:bg-white/10 hover:text-white"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 flex items-center justify-center relative select-none"
        style={{ backgroundColor: current.background_color || '#000' }}
        onPointerDown={() => setPaused(true)}
        onPointerUp={() => setPaused(false)}
        onPointerLeave={() => setPaused(false)}
      >
        {current.media_url && mediaUrl ? (
          current.media_type === 'video' ? (
            <video src={mediaUrl} className="max-h-full max-w-full" autoPlay playsInline />
          ) : (
            <img src={mediaUrl} alt="status" className="max-h-full max-w-full object-contain" />
          )
        ) : current.media_url && !mediaUrl ? (
          <Loader2 className="h-8 w-8 animate-spin text-white/70" />
        ) : null}
        {current.content && (
          <div className="absolute inset-0 flex items-center justify-center p-8 pointer-events-none">
            <p className="text-white text-2xl text-center font-medium drop-shadow-lg whitespace-pre-wrap">
              {current.content}
            </p>
          </div>
        )}

        {/* Tap zones */}
        <button
          type="button"
          aria-label="Previous"
          className="absolute left-0 top-0 bottom-0 w-1/3"
          onClick={goPrev}
        />
        <button
          type="button"
          aria-label="Next"
          className="absolute right-0 top-0 bottom-0 w-1/3"
          onClick={goNext}
        />

        {/* Side arrows on desktop */}
        <button
          type="button"
          className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-2 hover:bg-black/60"
          onClick={goPrev}
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-2 hover:bg-black/60"
          onClick={goNext}
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Footer */}
      {isOwn && viewCount !== null && (
        <div className="flex items-center justify-center gap-2 py-3 text-white/80 text-sm">
          <Eye className="h-4 w-4" />
          <span>
            {viewCount} {viewCount === 1 ? 'view' : 'views'}
          </span>
        </div>
      )}
    </div>
  );
}
